import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchApiAuth } from 'core/utils/api';
import { notification } from 'antd';
import { handleError } from 'core/utils/helpers';
import { REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING } from 'core/utils/value';
import { UploadFile } from 'antd/lib/upload/interface';
import { VisitRequestFormResponse, ChangeSelectedDateResponse, AmendActionResponse, VisitRequestVisitorCSVUploadResponse } from './types';
import { setLastRequestAmended } from './visitRequestFormSlice';

export const createVisitRequest = createAsyncThunk(
    'visitRequestForm/create',
    async (payload: { siteId?: number } | undefined, { rejectWithValue }) => {
        try {
            const requestOptions: { method: string; url: string; body?: object } = {
                method: 'POST',
                url: 'visit-request/create',
            };

            if (payload?.siteId) {
                requestOptions.body = { siteId: payload.siteId };
            }

            const response: VisitRequestFormResponse = await fetchApiAuth(requestOptions);

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const deleteVisitRequest = createAsyncThunk(
    'visitRequestForm/delete',
    async (id: string | number, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'DELETE',
                url: `visit-request/${id}`,
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                // Silent delete - don't show error notification to user
                console.error('Failed to delete draft request:', errorMessage);
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const loadVisitRequestForm = createAsyncThunk(
    'visitRequestForm/load',
    async (payload: { id: string | number, step: number, amending?: boolean }, { rejectWithValue }) => {
        try {
            const { id, step, amending = false } = payload;
            const response: VisitRequestFormResponse = await fetchApiAuth({
                method: 'GET',
                url: `visit-request/${id}/step/${step}?amending=${amending}`,
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const updateVisitRequestForm = createAsyncThunk(
    'visitRequestForm/update',
    async (payload: { id: string | number, step: number, data: object }, { rejectWithValue }) => {
        const { id, data } = payload;
        try {
            const response: VisitRequestFormResponse = await fetchApiAuth({
                method: 'PUT',
                url: `visit-request/${id}`,
                body: { ...data },
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const uploadVisitorCsv = createAsyncThunk(
    'visitRequestForm/uploadCsv',
    async (payload: { id: string | number, data: FormData }, { rejectWithValue }) => {
        const { id, data } = payload;
        try {
            const response: VisitRequestVisitorCSVUploadResponse = await fetchApiAuth({
                method: 'POST',
                url: `visit-request/${id}/upload-csv`,
                headers: { 'Content-Type': 'multipart/form-data;' },
                body: data,
            });

            notification.success({
                message: 'Success',
                description: 'The visitors were uploaded successfully.',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const amendActionRequest = createAsyncThunk(
    'visitRequestReview/amend',
    async (payload: { id: string | number, sections: string[], details: string }, { rejectWithValue, dispatch }) => {
        const { id, sections, details } = payload;

        try {
            const response: AmendActionResponse = await fetchApiAuth({
                method: 'POST',
                url: `visit-request/${id}/action`,
                body: {
                    action: 'amend',
                    sections,
                    details,
                },
            });

            // The amendment is committed, so there is nothing pending to clean
            // up. Leaving the marker set made the next dashboard / request
            // view visit cancel it and report "amendment has been cancelled".
            dispatch(setLastRequestAmended(null));

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const cancelAmendmentRequest = createAsyncThunk(
    'visitRequestReview/cancelAmend',
    async (payload: string | number | { id: string | number, type?: string }, { rejectWithValue, dispatch }) => {
        const id = typeof payload === 'object' ? payload.id : payload;
        const type = typeof payload === 'object' ? payload.type : undefined;
        try {
            const response: { data: { success: boolean, message: string } } = await fetchApiAuth({
                method: 'POST',
                url: `visit-request/${id}/action`,
                body: {
                    action: 'cancel-amend',
                },
            });

            let label = 'visit request';
            if (type === REQUEST_TYPE_ACCESS) label = 'access request';
            else if (type === REQUEST_TYPE_GUARDING) label = 'guarding request';
            notification.success({
                message: 'Success',
                description: `The ${label} amendment has been cancelled.`,
            });

            dispatch(setLastRequestAmended(null));

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const changeSelectedDate = createAsyncThunk(
    'visitRequestReview/changeSelectedDate',
    async (payload: { id: number, newDate: string }, { rejectWithValue }) => {
        const { id, newDate } = payload;
        try {
            const response: ChangeSelectedDateResponse = await fetchApiAuth({
                method: 'PUT',
                url: 'visit-request/change-selected-date',
                body: {
                    visitRequestDateId: id,
                    newDate,
                },
            });

            notification.success({
                message: 'Success',
                description: 'The date has been changed.',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const uploadAttachment = createAsyncThunk(
    'visitRequestReview/uploadAttachment',
    async (payload: { id: string, attachment: UploadFile }, { rejectWithValue }) => {
        const { id, attachment } = payload;
        const formData = new FormData();
        try {
            const file = attachment.originFileObj;
            if (file) {
                formData.append('file', file);
            }
            const response = await fetchApiAuth({
                method: 'POST',
                url: `visit-request/${id}/upload-attachment`,
                body: formData,
            });

            notification.success({
                message: 'Success',
                description: 'File uploaded successfully.',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const deleteAttachment = createAsyncThunk(
    'visitRequestReview/deleteAttachment',
    async (payload: { id: string, attachmentId: number }, { rejectWithValue }) => {
        const { id, attachmentId } = payload;
        try {
            const response = await fetchApiAuth({
                method: 'DELETE',
                url: `visit-request/${id}/delete-attachment/${attachmentId}`,
            });

            notification.success({
                message: 'Success',
                description: 'File successfully removed.',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);
